import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ShieldCheckIcon,
    UserPlusIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";
import { inviteAdmin, getPermissionsCatalog } from "../api/adminsApi";
import type { InviteAdminRequest } from "../types";
import { PermissionCatalogEditor } from "../ui/PermissionCatalogEditor";
import type { PermissionCode } from "@/features/auth/permissions";

const inputClassName = (hasError: boolean) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-900 transition placeholder:text-slate-400 focus:outline-none focus:ring-4 dark:bg-slate-950 dark:text-slate-100 ${
        hasError
            ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
            : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
    }`;

const getApiErrorCode = (error: unknown) => {
    const code = (error as { response?: { data?: { errorCode?: unknown } } })?.response?.data?.errorCode;
    return typeof code === "string" ? code : null;
};

export const CreateAdminRoute = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [selectedPermissions, setSelectedPermissions] = useState<PermissionCode[]>([]);
    const [formData, setFormData] = useState<InviteAdminRequest>({
        firstName: "",
        lastName: "",
        email: "",
        permissions: [],
    });
    const [errors, setErrors] = useState<Partial<Record<keyof InviteAdminRequest, string>>>({});

    const permissionCatalogQuery = useQuery({
        queryKey: ["permissions-catalog"],
        queryFn: getPermissionsCatalog,
    });

    const permissionCatalog = permissionCatalogQuery.data?.data.permissions ?? {};

    const inviteMutation = useMutation({
        mutationFn: inviteAdmin,
        onError: (error) => {
            if (getApiErrorCode(error) === "ADMIN_INVITATION_ALREADY_EXISTS") {
                queryClient.invalidateQueries({ queryKey: ["admins"] });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admins"] });
            navigate("/admin-management", {
                state: { flashMessage: "Invitation sent successfully." },
            });
        },
    });

    const validateForm = () => {
        const nextErrors: Partial<Record<keyof InviteAdminRequest, string>> = {};

        if (!formData.firstName.trim()) nextErrors.firstName = "First name is required";
        if (!formData.lastName.trim()) nextErrors.lastName = "Last name is required";
        if (!formData.email.trim()) {
            nextErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            nextErrors.email = "Invalid email format";
        }
        if (selectedPermissions.length === 0) {
            nextErrors.permissions = "Select at least one permission.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleChange = (field: keyof InviteAdminRequest, value: string) => {
        setFormData((previous) => ({ ...previous, [field]: value }));
        if (errors[field]) setErrors((previous) => ({ ...previous, [field]: undefined }));
        if (inviteMutation.isError) inviteMutation.reset();
    };

    const handlePermissionsChange = (permissions: PermissionCode[]) => {
        setSelectedPermissions(permissions);
        if (errors.permissions) setErrors((previous) => ({ ...previous, permissions: undefined }));
        if (inviteMutation.isError) inviteMutation.reset();
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!validateForm()) return;

        inviteMutation.mutate({
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            email: formData.email.trim(),
            permissions: selectedPermissions,
        });
    };

    const inviteErrorCode = getApiErrorCode(inviteMutation.error);

    return (
        <div className="mx-auto max-w-[1600px] space-y-7 pb-8">
            <header className="space-y-4">
                <Link
                    to="/admin-management"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 transition hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Admin Management
                </Link>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-primary-700 dark:bg-primary-900/20 dark:text-primary-300">
                            <UserPlusIcon className="h-3.5 w-3.5" />
                            New invitation
                        </div>
                        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                            Invite Admin
                        </h1>
                        <p className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                            Invite an administrator by email. They will receive a one-time code and choose their own password.
                        </p>
                    </div>
                    <div className="hidden items-center gap-2 text-xs text-slate-500 dark:text-slate-400 lg:flex">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 font-semibold text-white">1</span>
                        <span>Recipient</span>
                        <span className="h-px w-8 bg-slate-300 dark:bg-slate-700" />
                        <span className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 font-semibold text-slate-500 dark:border-slate-700 dark:text-slate-400">2</span>
                        <span>Permissions</span>
                    </div>
                </div>
            </header>

            <form onSubmit={handleSubmit} className="space-y-6">
                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-start gap-4 border-b border-slate-200 bg-slate-50/80 px-6 py-5 dark:border-slate-800 dark:bg-slate-800/40">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                            <UserPlusIcon className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-primary-600 dark:text-primary-400">Step 1</span>
                                <span className="text-slate-300 dark:text-slate-600">/</span>
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Recipient details</h2>
                            </div>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                Enter the person&apos;s name and work email. No password is collected here.
                            </p>
                        </div>
                    </div>

                    <div className="grid gap-5 p-6 md:grid-cols-2 lg:p-8">
                        <div>
                            <label htmlFor="first-name" className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">First name</label>
                            <input id="first-name" type="text" value={formData.firstName} onChange={(event) => handleChange("firstName", event.target.value)} placeholder="Jane" className={inputClassName(Boolean(errors.firstName))} />
                            {errors.firstName ? <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">{errors.firstName}</p> : null}
                        </div>
                        <div>
                            <label htmlFor="last-name" className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Last name</label>
                            <input id="last-name" type="text" value={formData.lastName} onChange={(event) => handleChange("lastName", event.target.value)} placeholder="Admin" className={inputClassName(Boolean(errors.lastName))} />
                            {errors.lastName ? <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">{errors.lastName}</p> : null}
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="admin-email" className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">Work email address</label>
                            <input id="admin-email" type="email" value={formData.email} onChange={(event) => handleChange("email", event.target.value)} placeholder="jane.admin@company.com" className={inputClassName(Boolean(errors.email))} />
                            {errors.email ? <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">{errors.email}</p> : null}
                        </div>
                    </div>
                </section>

                <div className="grid items-start gap-6 2xl:grid-cols-[minmax(0,1fr)_280px]">
                    <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="flex items-start gap-4 border-b border-slate-200 bg-slate-50/80 px-6 py-5 dark:border-slate-800 dark:bg-slate-800/40">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"><ShieldCheckIcon className="h-5 w-5" /></div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300">Step 2</span>
                                    <span className="text-slate-300 dark:text-slate-600">/</span>
                                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Permission assignment</h2>
                                </div>
                                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Choose the exact capabilities this administrator should have.</p>
                            </div>
                        </div>
                        <div className="p-6 lg:p-8">
                            {permissionCatalogQuery.isLoading ? (
                                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-14 text-center dark:border-slate-800 dark:bg-slate-900/50"><div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" /><p className="text-sm text-slate-500 dark:text-slate-400">Loading permission catalog...</p></div>
                            ) : permissionCatalogQuery.isError ? (
                                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-6 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300"><div className="flex items-start gap-3"><ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" /><div><p className="font-medium">Failed to load permission catalog.</p><p className="mt-1">Retry this page before sending the invitation.</p></div></div></div>
                            ) : (
                                <PermissionCatalogEditor catalog={permissionCatalog} selectedPermissions={selectedPermissions} onChange={handlePermissionsChange} disabled={inviteMutation.isPending} hideIntro layout="workspace" />
                            )}
                            {errors.permissions ? <p className="mt-4 text-sm text-rose-600 dark:text-rose-400">{errors.permissions}</p> : null}
                        </div>
                    </section>

                    <aside className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/60 2xl:sticky 2xl:top-6">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white"><CheckCircleIcon className="h-5 w-5 text-emerald-500" />Invitation checklist</div>
                        <div className="mt-5 space-y-4">
                            <div className="flex items-start gap-3"><span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-primary-500" /><div><p className="text-sm font-medium text-slate-700 dark:text-slate-200">Recipient details</p><p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">The invitee will activate the account using their email and OTP.</p></div></div>
                            <div className="flex items-start gap-3"><span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${selectedPermissions.length ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`} /><div><p className="text-sm font-medium text-slate-700 dark:text-slate-200">Access scope</p><p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{selectedPermissions.length ? `${selectedPermissions.length} permission${selectedPermissions.length === 1 ? "" : "s"} selected.` : "Select at least one permission."}</p></div></div>
                        </div>
                        <div className="mt-6 border-t border-slate-200 pt-4 text-xs leading-5 text-slate-500 dark:border-slate-800 dark:text-slate-400">The invitation expires after 24 hours. A resend invalidates the previous OTP.</div>
                    </aside>
                </div>

                {inviteMutation.isError ? (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                        {inviteErrorCode === "ADMIN_EMAIL_EXISTS" ? (
                            <span>This email already belongs to an admin account. <Link to="/auth/login" className="font-semibold underline">Ask them to sign in instead.</Link></span>
                        ) : inviteErrorCode === "ADMIN_INVITATION_ALREADY_EXISTS" ? (
                            <span>An invitation already exists for this email. <Link to="/admin-management" className="font-semibold underline">Return to the admin list to manage it.</Link></span>
                        ) : inviteErrorCode === "INVALID_PERMISSION" ? (
                            "One or more selected permissions are no longer available. Refresh the permission catalog and review the selection."
                        ) : (
                            extractApiError(inviteMutation.error)
                        )}
                    </div>
                ) : null}

                <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 pt-5 dark:border-slate-800">
                    <Button type="button" variant="outline" onClick={() => navigate("/admin-management")} disabled={inviteMutation.isPending}>Cancel</Button>
                    <Button type="submit" disabled={inviteMutation.isPending}>{inviteMutation.isPending ? "Sending invitation..." : "Send invitation"}</Button>
                </div>
            </form>
        </div>
    );
};
